import { SelectionActionPopover } from 'module/ui/components/atoms/rich-text-editor/editor-popover/selection-action-popover';
import { EditorPopoverContentProps } from 'module/ui/components/atoms/rich-text-editor/editor-popover/types';
import { BasicFormattingCommands } from 'module/ui/components/atoms/rich-text-editor/toolbar/basic-formatting-commands';
import { Editor, isNodeSelection } from '@tiptap/core';
import { Divider } from 'antd';
import { PageType } from 'api/page-template/types';
import { FC, useCallback } from 'react';
import styled from 'styled-components';
import { AskAiCommandsDropdown } from './ask-ai-commands';
import { EnhanceAINoteButton } from './enhance-ai-command';
import { DefaultFloatingToolbarConfig } from './types';
import {
  ItalicButton,
  StrikethroughButton,
  UnderlineButton,
} from '../../../../module/ui/components/atoms/rich-text-editor/toolbar/basic-mark-commands';
import { BoldButton } from '../../../../module/ui/components/atoms/rich-text-editor/toolbar/basic-mark-commands';

interface EditorDefaultToolbar {
  editor: Editor;
  closePopover: () => void;
  config?: DefaultFloatingToolbarConfig;
}
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-02);
`;

const SEMANTIC_PAGE_TYPES = [
  PageType.Meeting,
  PageType.Email,
  PageType.Company,
];

export const EditorDefaultToolbar: FC<EditorDefaultToolbar> = ({
  editor,
  closePopover,
  config,
}) => {
  const canShow = useCallback((editor: Editor) => {
    // Check if the current selection is inside a table
    const { selection } = editor.state;
    const $pos = selection.$from;

    // Walk up the tree to find if we're in a table context
    for (let depth = $pos.depth; depth > 0; depth--) {
      const node = $pos.node(depth);

      // Check if the node has a tableRole property (indicating it's part of a table)
      if (node.type.spec.tableRole) {
        return false; // Don't show toolbar inside tables
      }
    }

    return true; // Show toolbar outside of tables
  }, []);

  if (!canShow(editor)) {
    return null;
  }

  const isSemanticPage = SEMANTIC_PAGE_TYPES.includes(
    config?.pageType as PageType,
  );

  return (
    <Wrapper>
      {config?.enableAskAiCommands && canShow(editor) && (
        <>
          <EnhanceAINoteButton
            editor={editor}
            afterCommandExecuted={closePopover}
          />
          {isSemanticPage && (
            <>
              <Divider type="vertical" />
              <AskAiCommandsDropdown
                editor={editor}
                afterCommandExecuted={closePopover}
              />
            </>
          )}
          <Divider type="vertical" />
        </>
      )}
      <BasicFormattingCommands
        editor={editor}
        afterCommandExecuted={closePopover}
      />
      <BoldButton editor={editor} afterCommandExecuted={closePopover} />
      <ItalicButton editor={editor} afterCommandExecuted={closePopover} />
      <UnderlineButton editor={editor} afterCommandExecuted={closePopover} />
      <StrikethroughButton
        editor={editor}
        afterCommandExecuted={closePopover}
      />
    </Wrapper>
  );
};
interface FloatingToolbarProps {
  editor: Editor;
  config?: DefaultFloatingToolbarConfig;
}
export const FloatingToolbar: FC<FloatingToolbarProps> = ({
  editor,
  config,
}) => {
  const canShow = useCallback((editor: Editor) => {
    if (!editor.isEditable) {
      return false;
    }

    const { selection } = editor.state;
    const { from, to } = selection;

    // Don't show floating toolbar when only image(s) selected (e.g. after paste)
    if (isNodeSelection(selection) && selection.node?.type.name === 'image') {
      return false;
    }

    // Range selection: hide when selection has no text and contains at least one image
    if (from !== to) {
      const text = editor.state.doc.textBetween(from, to, undefined, ' ');
      let hasImage = false;

      editor.state.doc.nodesBetween(from, to, (node) => {
        if (node.type.name === 'image') {
          hasImage = true;
        }
      });

      if (hasImage && !text.trim()) {
        return false;
      }
    }

    return true;
  }, []);

  const renderContent = useCallback(
    ({ editor, closePopover }: EditorPopoverContentProps) => (
      <EditorDefaultToolbar
        editor={editor}
        closePopover={closePopover}
        config={config}
      />
    ),
    [config],
  );

  return (
    <SelectionActionPopover
      popoverContentContainerClassName={'test'}
      editor={editor}
      content={renderContent}
      canShow={canShow}
    />
  );
};
